import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Alert,
  AlertDescription,
} from '@roger/ui-components';
import { Copy, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import type { AddUserResponseWithData } from '../../services/rogerDocApi';

interface AddedUserCardProps {
  user: AddUserResponseWithData;
  onDismiss: () => void;
}

export const AddedUserCard: React.FC<AddedUserCardProps> = ({
  user,
  onDismiss,
}) => {
  const [showPassword, setShowPassword] = useState(true);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const credentialsText = `Username: ${user.username}\nPassword: ${user.password}`;
    navigator.clipboard.writeText(credentialsText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Alert className='bg-green-50 border-green-200'>
      <CheckCircle2 className='h-4 w-4 text-green-600' />
      <AlertDescription>
        <div className='space-y-3'>
          <div>
            <h3 className='font-semibold text-green-900'>
              User Created Successfully!
            </h3>
            <p className='text-sm text-green-700 mt-1'>
              {user.first_name} {user.last_name} ({user.clinician_title})
            </p>
          </div>

          <Card className='bg-white'>
            <CardContent className='p-4 space-y-3'>
              <div>
                <label className='text-xs font-medium text-gray-600'>
                  Username
                </label>
                <p className='font-mono text-sm mt-1'>{user.username}</p>
              </div>

              <div>
                <label className='text-xs font-medium text-gray-600'>
                  Password
                </label>
                <div className='flex items-center gap-2 mt-1'>
                  <p className='font-mono text-sm flex-1'>
                    {showPassword ? user.password : '••••••••••••'}
                  </p>
                  <Button
                    size='sm'
                    variant='ghost'
                    onClick={() => setShowPassword(!showPassword)}
                    className='h-7 w-7 p-0'
                  >
                    {showPassword ? (
                      <EyeOff className='h-4 w-4' />
                    ) : (
                      <Eye className='h-4 w-4' />
                    )}
                  </Button>
                </div>
              </div>

              <div className='flex gap-2 pt-2'>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={handleCopy}
                  className='flex-1'
                  disabled={copied}
                >
                  <Copy className='h-4 w-4' />
                  {copied ? 'Copied!' : 'Copy Credentials'}
                </Button>
                <Button size='sm' variant='outline' onClick={onDismiss}>
                  Dismiss
                </Button>
              </div>

              <p className='text-xs text-amber-600 mt-2'>
                ⚠️ Save these credentials now. The password will not be shown
                again.
              </p>
            </CardContent>
          </Card>
        </div>
      </AlertDescription>
    </Alert>
  );
};
